import { memo, useState, useEffect, useMemo } from "react";
import { FormControl, FormHelperText, TextField } from "@mui/material";

import classNames from "common/class-names";

import { AppLinkInputProps } from "dto/components/app-link-input-dto";

import addIcon from "assets/images/components/app-link-input/add-icon.svg";
import addIconBox from "assets/images/components/app-link-input/add-icon-box.svg";
import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";

const AppLinkInputField = ({ label, required, onChange, inputValues = [], disabled = false, errors, touched }: AppLinkInputProps) => {
	const [textFields, setTextFields] = useState<string[]>(inputValues);

	const isInvalid = useMemo(() => Boolean(errors) && Boolean(touched), [errors, touched]);

	const addTextField = () => {
		if (!textFields.includes("")) {
			setTextFields([...textFields, ""]);
		}
	};

	const removeTextField = (index: number) => {
		const filterFields = textFields.filter((_, i) => i !== index);

		setTextFields(filterFields);

		if (onChange) onChange(filterFields);
	};

	const onHandleTextFieldChange = (index: number, value: string) => {
		const updatedFields = [...textFields];

		updatedFields[index] = value.trim();

		setTextFields(updatedFields);

		if (onChange) onChange(updatedFields);
	};

	const onHandleBlur = (index: number) => {
		if (textFields[index]?.trim() === "") setTextFields((prev) => prev.filter((f) => f.trim() !== ""));
	};

	const addButtonClasses = classNames({
		"link-input__add-button": true,
		"link-input__add-button--centered": textFields.length === 0,
	});

	const bodyClassNames = useMemo(() => classNames({ "link-input__body": true, "link-input__body--error": isInvalid, "link-input__body--disabled": disabled }), [disabled, isInvalid]);

	useEffect(() => {
		setTextFields(inputValues);
	}, [inputValues]);

	return (
		<div className="app-link-input">
			<FormControl className="link-input__form-control">
				<label className="link-input__label">
					{label}

					{required && <span className="link-input__required"> *</span>}
				</label>

				<div className={bodyClassNames}>
					{textFields.map((field, index) => (
						<div key={index} className="link-input__field">
							<TextField
								disabled={disabled}
								title={field}
								name={`textField-${index}`}
								className="link-input__textfield"
								placeholder={field}
								value={field}
								onChange={(e) => onHandleTextFieldChange(index, e.target.value)}
								onBlur={() => onHandleBlur(index)}
								autoFocus={field === ""}
								slotProps={{
									input: {
										endAdornment: !disabled && (
											<div onClick={() => removeTextField(index)} className="link-input__icon">
												<img src={wasteBinIcon} alt="Remove" />
											</div>
										),
									},
								}}
							/>
						</div>
					))}

					{!disabled && (
						<div onClick={addTextField} className={addButtonClasses}>
							<img src={textFields.length === 0 ? addIcon : addIconBox} alt="Add" />

							{textFields.length === 0 && <span className="link-input__text">Add New</span>}
						</div>
					)}
				</div>

				{isInvalid && <FormHelperText error>{errors}</FormHelperText>}
			</FormControl>
		</div>
	);
};

export default memo(AppLinkInputField);
